(() => {
'use strict';

class MPPhotoStudio extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mpReady === 'true') return;
    this.dataset.mpReady = 'true';
    this.mode = this.dataset.mode || 'standard';
    this.form = this.querySelector('[data-mp-form]');
    this.sourceInput = this.querySelector('[data-mp-source]');
    this.outputInput = this.querySelector('[data-mp-output]');
    this.canvas = this.querySelector('[data-mp-canvas]');
    this.paper = this.querySelector('[data-mp-paper]');
    this.stage = this.querySelector('[data-mp-stage]');
    this.zoomInput = this.querySelector('[data-mp-zoom]');
    this.zoomValue = this.querySelector('[data-mp-zoom-value]');
    this.submitButton = this.querySelector('[data-mp-submit]');
    this.fileName = this.querySelector('[data-mp-filename]');
    this.error = this.querySelector('[data-mp-error]');
    this.price = this.querySelector('[data-mp-price]');
    this.variantInput = this.querySelector('[data-mp-variant-id]');
    this.orientationInput = this.querySelector('[data-mp-orientation-value]');
    this.dpiInput = this.querySelector('[data-mp-dpi-value]');
    this.borderInput = this.querySelector('[data-mp-border-value]');
    this.summaryInput = this.querySelector('[data-mp-summary]');
    this.review = this.querySelector('[data-mp-review]');
    this.qualityNodes = [...this.querySelectorAll('[data-mp-quality]')];
    this.qualityTextNodes = [...this.querySelectorAll('[data-mp-quality-text]')];
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.variants = JSON.parse(this.querySelector('[data-mp-variants]').textContent || '[]');
    this.image = null;
    this.imageUrl = null;
    this.orientation = 'portrait';
    this.productType = 'standard';
    this.filter = 'none';
    this.overlayText = '';
    this.textDesign = 'Klassisk';
    this.textColor = '#111111';
    this.scale = 1;
    this.minScale = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.drag = null;
    this.preparing = false;
    this.dragMoved = false;
    this.editorOpen = false;
    this.activePointers = new Map();
    this.pinch = null;

    this.sourceInput.addEventListener('change', () => this.loadFile());
    this.zoomInput.addEventListener('input', () => { this.hideEditorHint(); this.setZoom(Number(this.zoomInput.value)); });
    this.querySelectorAll('[data-mp-zoom-step]').forEach((button) => button.addEventListener('click', () => {
      this.hideEditorHint();
      const value = Math.max(100, Math.min(300, Number(this.zoomInput.value) + Number(button.dataset.mpZoomStep)));
      this.zoomInput.value = String(value);
      this.setZoom(value);
    }));
    this.querySelectorAll('[data-mp-orientation]').forEach((input) => input.addEventListener('change', () => this.changeOrientation(input.value)));
    this.querySelectorAll('[data-mp-editor-orientation]').forEach((button) => button.addEventListener('click', () => {
      const input = [...this.querySelectorAll('[data-mp-orientation]')].find((item) => item.value === button.dataset.mpEditorOrientation);
      if (!input) return;
      input.checked = true;
      this.changeOrientation(input.value);
    }));
    this.querySelectorAll('[data-mp-option]').forEach((input) => input.addEventListener('change', () => this.optionsChanged()));
    this.querySelectorAll('[data-mp-product-type]').forEach((input) => input.addEventListener('change', () => this.changeProductType(input)));
    this.querySelectorAll('[data-mp-nudge]').forEach((button) => button.addEventListener('click', () => this.nudgeImage(button.dataset.mpNudge)));
    this.querySelectorAll('[data-mp-preview-upload]').forEach((button) => button.addEventListener('click', () => this.sourceInput.click()));
    this.querySelectorAll('[data-mp-open-editor]').forEach((button) => button.addEventListener('click', () => this.openEditor()));
    this.querySelectorAll('[data-mp-close-editor]').forEach((button) => button.addEventListener('click', () => this.closeEditor()));
    this.querySelectorAll('[data-mp-reset-image]').forEach((button) => button.addEventListener('click', () => this.fitImage(true)));
    const textInput = this.querySelector('[data-mp-overlay-text]');
    if (textInput) textInput.addEventListener('input', () => { this.overlayText = textInput.value; this.draw(); this.updateReview(); });
    this.querySelectorAll('[data-mp-text-design]').forEach((input) => input.addEventListener('change', () => {
      this.textDesign = input.value;
      document.fonts?.load(this.getTextFont().css).then(() => this.draw()).catch(() => this.draw());
      this.updateReview();
    }));
    this.querySelectorAll('[data-mp-text-color]').forEach((input) => input.addEventListener('change', () => {
      this.textColor = input.dataset.color || '#111111';
      this.draw();
      this.updateReview();
    }));
    this.canvas.addEventListener('pointerdown', (event) => this.startDrag(event));
    this.canvas.addEventListener('pointermove', (event) => this.moveDrag(event));
    this.canvas.addEventListener('pointerup', (event) => this.endDrag(event));
    this.canvas.addEventListener('pointercancel', (event) => this.endDrag(event));
    this.canvas.addEventListener('click', () => {
      if (this.image && !this.dragMoved && !this.editorOpen) this.openEditor();
      this.dragMoved = false;
    });
    this.canvas.addEventListener('wheel', (event) => this.wheelZoom(event), { passive: false });
    window.addEventListener('resize', () => this.resizeCanvas());
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && this.editorOpen) this.closeEditor(); });
    this.form.addEventListener('submit', (event) => this.prepareSubmit(event), true);

    this.changeProductType(this.querySelector('[data-mp-product-type]:checked'));
    this.updateGeometry();
    this.resolveVariant();
    this.updateReview();
  }

  async loadFile() {
    const file = this.sourceInput.files && this.sourceInput.files[0];
    this.clearError();
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return this.rejectFile('Välj en bild i JPG-, PNG- eller WebP-format.');
    if (file.size > 25 * 1024 * 1024) return this.rejectFile('Bilden är större än 25 MB. Välj en mindre fil.');
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
    this.imageUrl = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      this.image = image;
      this.fileName.textContent = file.name;
      this.classList.add('has-image');
      this.stage.style.setProperty('--mp-preview-image', `url("${this.imageUrl}")`);
      this.changeOrientation(this.querySelector('[data-mp-orientation]:checked')?.value || 'Stående');
      this.resolveVariant();
      this.updateQuality();
      this.openEditor();
    };
    image.onerror = () => this.rejectFile('Bilden kunde inte öppnas. Prova en annan fil.');
    image.src = this.imageUrl;
  }

  rejectFile(message) { this.sourceInput.value = ''; this.showError(message); }

  optionsChanged() {
    if (this.productType === 'canvas') this.syncCanvasSizeForOrientation();
    this.updateGeometry();
    this.resolveVariant();
    this.updateQuality();
    this.updateReview();
  }

  changeOrientation(value) {
    this.orientation = /^(landscape|liggande)$/i.test(value) ? 'landscape' : 'portrait';
    this.orientationInput.value = this.orientation === 'landscape' ? 'Liggande' : 'Stående';
    if (this.productType === 'canvas') this.syncCanvasSizeForOrientation();
    this.syncEditorOrientation();
    this.updateGeometry();
    this.resolveVariant();
    this.updateQuality();
    this.updateReview();
  }

  syncEditorOrientation() {
    const selected = this.orientation === 'landscape' ? 'Liggande' : 'Stående';
    this.querySelectorAll('[data-mp-editor-orientation]').forEach((button) => {
      const active = button.dataset.mpEditorOrientation === selected;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  parseDimensions(value) {
    const match = String(value || '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i);
    if (!match) return null;
    return [Number(match[1].replace(',', '.')), Number(match[2].replace(',', '.'))];
  }

  syncCanvasSizeForOrientation() {
    // A separate Shopify orientation option already maps its own variants.
    if (this.querySelector('[data-mp-orientation][data-mp-option]')) return;

    const sizeInputs = [...this.querySelectorAll('[data-mp-option]')]
      .filter((input) => this.parseDimensions(input.value));
    if (!sizeInputs.length) return;

    // Only one size button is shown per pair. The mirrored value remains in the
    // form and is selected behind the scenes so Shopify receives the right SKU.
    sizeInputs.forEach((input) => {
      const [width, height] = this.parseDimensions(input.value);
      const label = input.closest('label');
      if (label) label.hidden = width > height;
    });

    const selected = sizeInputs.find((input) => input.checked) || sizeInputs[0];
    const selectedDimensions = this.parseDimensions(selected.value);
    if (!selectedDimensions) return;
    const pairKey = selectedDimensions.slice().sort((a, b) => a - b).join('x');
    const wantsLandscape = this.orientation === 'landscape';
    const target = sizeInputs.find((input) => {
      const dimensions = this.parseDimensions(input.value);
      const samePair = dimensions.slice().sort((a, b) => a - b).join('x') === pairKey;
      return samePair && (wantsLandscape ? dimensions[0] >= dimensions[1] : dimensions[0] <= dimensions[1]);
    });
    if (target) target.checked = true;

    sizeInputs.forEach((input) => input.closest('label')?.classList.remove('is-linked-selected'));
    const visiblePartner = sizeInputs.find((input) => {
      const dimensions = this.parseDimensions(input.value);
      return dimensions[0] <= dimensions[1] && dimensions.slice().sort((a, b) => a - b).join('x') === pairKey;
    });
    visiblePartner?.closest('label')?.classList.add('is-linked-selected');
  }

  getSelectedOptions() {
    return [...this.querySelectorAll('[data-mp-option]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition || 99) - Number(b.dataset.optionPosition || 99));
  }

  getSelectedSize() {
    for (const input of this.getSelectedOptions()) {
      const dimensions = this.parseDimensions(input.value);
      if (dimensions) return dimensions;
    }
    return [21, 30];
  }

  hasWhiteBorder() { return this.productType === 'passepartout' || this.productType === 'text'; }

  getBorderMm() {
    const size = this.getSelectedSize().slice().sort((a, b) => a - b).join('x');
    return ({ '13x18': 15, '21x30': 20, '30x40': 23, '50x70': 40, '70x100': 60, '100x140': 80 })[size] || 20;
  }

  getBordersCm() {
    if (!this.hasWhiteBorder()) return { left: 0, right: 0, top: 0, bottom: 0 };
    const regular = this.getBorderMm() / 10;
    // Textpostern behåller korrekt passepartout på sidorna och upptill,
    // men får en kompakt nederkant där textfältet ritas.
    if (this.productType === 'text') return { left: regular, right: regular, top: regular, bottom: .7 };
    return { left: regular, right: regular, top: regular, bottom: regular };
  }

  getBorderDescription() {
    if (!this.hasWhiteBorder()) return 'Ingen';
    if (this.productType === 'text') return `${this.getBorderMm()} mm på sidorna/ovankant · kompakt nederkant med text`;
    return `${this.getBorderMm()} mm`;
  }

  getPhysicalSize() {
    let [width, height] = this.getSelectedSize();
    if (this.orientation === 'landscape' && width < height) [width, height] = [height, width];
    if (this.orientation === 'portrait' && width > height) [width, height] = [height, width];
    return [width, height];
  }

  updateGeometry() {
    const [width, height] = this.getPhysicalSize();
    this.paper.style.aspectRatio = `${width} / ${height}`;
    this.paper.classList.toggle('is-landscape', this.orientation === 'landscape');
    const border = this.hasWhiteBorder();
    const borders = this.getBordersCm();
    this.paper.classList.toggle('has-passepartout', border);
    this.paper.style.setProperty('--mp-pad-left', `${Math.min(42, borders.left / width * 100)}%`);
    this.paper.style.setProperty('--mp-pad-right', `${Math.min(42, borders.right / width * 100)}%`);
    this.paper.style.setProperty('--mp-pad-top', `${Math.min(42, borders.top / height * 100)}%`);
    this.paper.style.setProperty('--mp-pad-bottom', `${Math.min(42, borders.bottom / height * 100)}%`);
    this.borderInput.value = this.getBorderDescription();
    const info = this.querySelector('[data-mp-border-info]');
    if (info) info.textContent = border
      ? (this.productType === 'text'
        ? `Vald storlek får ${this.getBorderMm()} mm passepartout på sidorna och upptill samt ett kompakt textfält nedtill.`
        : `Vald storlek får ${this.getBorderMm()} mm vitkant runt bilden.`)
      : 'Vitkanten anpassas automatiskt efter vald posterstorlek.';
    requestAnimationFrame(() => this.resizeCanvas(true));
  }

  resizeCanvas(reset = false, preservedState = null) {
    const viewState = preservedState || (!reset && this.image ? this.getViewState() : null);
    const rect = this.canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * ratio));
    const height = Math.max(1, Math.round(rect.height * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    if (this.image) {
      if (reset || !viewState) this.fitImage(true);
      else this.restoreViewState(viewState);
    } else this.draw();
  }

  getViewState() {
    if (!this.image || !this.scale) return null;
    const imageWidth = this.image.naturalWidth * this.scale;
    const imageHeight = this.image.naturalHeight * this.scale;
    return {
      zoom: Number(this.zoomInput.value) || 100,
      centerX: imageWidth ? (this.canvas.width / 2 - this.offsetX) / imageWidth : .5,
      centerY: imageHeight ? (this.canvas.height / 2 - this.offsetY) / imageHeight : .5
    };
  }

  restoreViewState(state) {
    if (!this.image || !state) return this.fitImage(true);
    this.minScale = Math.max(this.canvas.width / this.image.naturalWidth, this.canvas.height / this.image.naturalHeight);
    this.zoomInput.value = String(Math.max(100, Math.min(300, state.zoom)));
    this.scale = this.minScale * (Number(this.zoomInput.value) / 100);
    const imageWidth = this.image.naturalWidth * this.scale;
    const imageHeight = this.image.naturalHeight * this.scale;
    this.offsetX = this.canvas.width / 2 - state.centerX * imageWidth;
    this.offsetY = this.canvas.height / 2 - state.centerY * imageHeight;
    this.constrainOffsets();
    this.zoomValue.textContent = `${this.zoomInput.value}%`;
    this.draw();
  }

  openEditor() {
    if (!this.image || this.editorOpen) return;
    const state = this.getViewState();
    this.editorOpen = true;
    this.classList.add('is-editor-open');
    this.querySelector('[data-mp-editor-hint]')?.classList.remove('is-hidden');
    document.documentElement.classList.add('mp-photo-studio-lock');
    requestAnimationFrame(() => this.resizeCanvas(false, state));
  }

  closeEditor() {
    if (!this.editorOpen) return;
    const state = this.getViewState();
    this.editorOpen = false;
    this.classList.remove('is-editor-open');
    document.documentElement.classList.remove('mp-photo-studio-lock');
    requestAnimationFrame(() => this.resizeCanvas(false, state));
  }

  fitImage(resetZoom = true) {
    if (!this.image) return;
    this.minScale = Math.max(this.canvas.width / this.image.naturalWidth, this.canvas.height / this.image.naturalHeight);
    if (resetZoom) {
      this.zoomInput.value = '100';
      this.scale = this.minScale;
      this.offsetX = (this.canvas.width - this.image.naturalWidth * this.scale) / 2;
      this.offsetY = (this.canvas.height - this.image.naturalHeight * this.scale) / 2;
    } else {
      this.scale = Math.max(this.scale, this.minScale);
      this.constrainOffsets();
    }
    this.zoomValue.textContent = `${this.zoomInput.value}%`;
    this.draw();
  }

  setZoom(percent, anchorX = this.canvas.width / 2, anchorY = this.canvas.height / 2) {
    this.zoomValue.textContent = `${percent}%`;
    if (!this.image) return;
    const oldWidth = this.image.naturalWidth * this.scale;
    const oldHeight = this.image.naturalHeight * this.scale;
    const centerX = oldWidth ? (anchorX - this.offsetX) / oldWidth : .5;
    const centerY = oldHeight ? (anchorY - this.offsetY) / oldHeight : .5;
    this.scale = this.minScale * (percent / 100);
    const newWidth = this.image.naturalWidth * this.scale;
    const newHeight = this.image.naturalHeight * this.scale;
    this.offsetX = anchorX - centerX * newWidth;
    this.offsetY = anchorY - centerY * newHeight;
    this.constrainOffsets();
    this.draw();
  }

  constrainOffsets() {
    const width = this.image.naturalWidth * this.scale;
    const height = this.image.naturalHeight * this.scale;
    this.offsetX = width <= this.canvas.width ? (this.canvas.width - width) / 2 : Math.min(0, Math.max(this.canvas.width - width, this.offsetX));
    this.offsetY = height <= this.canvas.height ? (this.canvas.height - height) / 2 : Math.min(0, Math.max(this.canvas.height - height, this.offsetY));
  }

  startDrag(event) {
    if (!this.image) return;
    event.preventDefault();
    this.canvas.setPointerCapture(event.pointerId);
    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.hideEditorHint();
    this.dragMoved = false;
    if (this.activePointers.size === 1) {
      this.drag = { x: event.clientX, y: event.clientY, offsetX: this.offsetX, offsetY: this.offsetY };
      this.pinch = null;
    } else if (this.activePointers.size === 2) {
      const points = [...this.activePointers.values()];
      this.drag = null;
      this.pinch = {
        distance: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
        zoom: Number(this.zoomInput.value) || 100
      };
    }
  }
  moveDrag(event) {
    if (!this.activePointers.has(event.pointerId)) return;
    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.activePointers.size >= 2 && this.pinch) {
      const points = [...this.activePointers.values()].slice(0, 2);
      const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      const value = Math.max(100, Math.min(300, Math.round(this.pinch.zoom * distance / Math.max(1, this.pinch.distance))));
      const rect = this.canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const anchorX = ((points[0].x + points[1].x) / 2 - rect.left) * ratio;
      const anchorY = ((points[0].y + points[1].y) / 2 - rect.top) * ratio;
      this.zoomInput.value = String(value);
      this.setZoom(value, anchorX, anchorY);
      this.dragMoved = true;
      return;
    }
    if (!this.drag) return;
    if (Math.abs(event.clientX - this.drag.x) > 4 || Math.abs(event.clientY - this.drag.y) > 4) this.dragMoved = true;
    const ratio = window.devicePixelRatio || 1;
    this.offsetX = this.drag.offsetX + (event.clientX - this.drag.x) * ratio;
    this.offsetY = this.drag.offsetY + (event.clientY - this.drag.y) * ratio;
    this.constrainOffsets(); this.draw();
  }
  endDrag(event) {
    if (event) this.activePointers.delete(event.pointerId);
    this.pinch = null;
    const remaining = [...this.activePointers.values()][0];
    this.drag = remaining ? { x: remaining.x, y: remaining.y, offsetX: this.offsetX, offsetY: this.offsetY } : null;
  }
  wheelZoom(event) {
    if (!this.image) return;
    event.preventDefault();
    this.hideEditorHint();
    const value = Math.max(100, Math.min(300, Number(this.zoomInput.value) + (event.deltaY > 0 ? -5 : 5)));
    const rect = this.canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const anchorX = (event.clientX - rect.left) * ratio;
    const anchorY = (event.clientY - rect.top) * ratio;
    this.zoomInput.value = String(value); this.setZoom(value, anchorX, anchorY);
  }

  nudgeImage(direction) {
    if (!this.image) return;
    this.hideEditorHint();
    const amount = Math.max(8, Math.round(Math.min(this.canvas.width, this.canvas.height) * .035));
    if (direction === 'left') this.offsetX -= amount;
    if (direction === 'right') this.offsetX += amount;
    if (direction === 'up') this.offsetY -= amount;
    if (direction === 'down') this.offsetY += amount;
    this.constrainOffsets();
    this.draw();
  }

  hideEditorHint() {
    this.querySelector('[data-mp-editor-hint]')?.classList.add('is-hidden');
  }

  draw() {
    this.ctx.fillStyle = '#fff';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    if (!this.image) return;
    this.ctx.save();
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
    this.ctx.filter = this.filter;
    this.ctx.drawImage(this.image, this.offsetX, this.offsetY, this.image.naturalWidth * this.scale, this.image.naturalHeight * this.scale);
    this.ctx.restore();
    this.drawOverlayText(this.ctx, this.canvas.width, this.canvas.height, 0, 0, true);
    this.updateQuality();
  }

  drawOverlayText(context, width, height, originX = 0, originY = 0, showPlaceholder = false) {
    const isPlaceholder = showPlaceholder && this.productType === 'text' && !this.overlayText.trim();
    const displayText = isPlaceholder ? 'Skriv din text här' : this.overlayText.trim();
    if (!displayText) return;
    const font = this.getTextFont();
    const sizeFactor = this.textDesign === 'Modern' ? .046 : this.textDesign === 'Elegant' ? .038 : .034;
    let size = Math.max(16, Math.round(width * sizeFactor));
    const stripHeight = Math.max(size * 2.05, height * .105);
    const maxWidth = width * .84;
    context.save();
    context.fillStyle = '#fff';
    context.fillRect(originX, originY + height - stripHeight, width, stripHeight);
    context.font = `${font.style} ${font.weight} ${size}px ${font.family}`;
    while (size > 12 && context.measureText(displayText).width > maxWidth) {
      size -= 1;
      context.font = `${font.style} ${font.weight} ${size}px ${font.family}`;
    }
    context.textAlign = 'center'; context.textBaseline = 'middle';
    context.fillStyle = isPlaceholder ? '#777777' : this.textColor;
    context.fillText(displayText, originX + width / 2, originY + height - stripHeight / 2, maxWidth);
    context.restore();
  }

  getTextFont() {
    const input = this.querySelector('[data-mp-text-design]:checked');
    const familyName = input?.dataset.fontFamily || 'Georgia';
    const fallback = input?.dataset.fontFallback || 'serif';
    const family = `"${String(familyName).replace(/"/g, '')}", ${fallback}`;
    const weight = input?.dataset.fontWeight || (this.textDesign === 'Modern' ? '700' : '400');
    const style = input?.dataset.fontStyle || (this.textDesign === 'Elegant' ? 'italic' : 'normal');
    return { family, weight, style, css: `${style} ${weight} 32px ${family}` };
  }

  changeProductType(input) {
    if (!input) return;
    this.productType = input.dataset.kind || 'standard';
    this.filter = input.dataset.filter || 'none';
    const wrap = this.querySelector('[data-mp-text-wrap]');
    const field = this.querySelector('[data-mp-overlay-text]');
    const enabled = this.productType === 'text';
    this.classList.toggle('is-text-style', enabled);
    if (this.productType === 'canvas') this.syncCanvasSizeForOrientation();
    this.borderInput.disabled = !this.hasWhiteBorder();
    if (wrap) wrap.hidden = !enabled;
    if (field) {
      field.disabled = !enabled;
      field.placeholder = 'Skriv din text här';
      this.overlayText = enabled ? field.value : '';
    }
    const selectedDesign = this.querySelector('[data-mp-text-design]:checked');
    const selectedColor = this.querySelector('[data-mp-text-color]:checked');
    if (selectedDesign) this.textDesign = selectedDesign.value;
    if (selectedColor) this.textColor = selectedColor.dataset.color || '#111111';
    this.updateGeometry(); this.draw(); this.updateReview();
  }

  calculateDpi() {
    if (!this.image) return 0;
    let [widthCm, heightCm] = this.getPhysicalSize();
    if (this.hasWhiteBorder()) {
      const borders = this.getBordersCm();
      widthCm -= borders.left + borders.right;
      heightCm -= borders.top + borders.bottom;
    }
    const usedWidth = Math.min(this.image.naturalWidth, this.canvas.width / this.scale);
    const usedHeight = Math.min(this.image.naturalHeight, this.canvas.height / this.scale);
    return Math.max(0, Math.floor(Math.min(usedWidth / (widthCm / 2.54), usedHeight / (heightCm / 2.54))));
  }

  updateQuality() {
    const dpi = this.calculateDpi();
    this.dpiInput.value = dpi ? String(dpi) : '';
    if (!this.image) return this.setQuality('neutral', 'Ladda upp ett foto för att kontrollera bildkvaliteten.');
    if (dpi >= 180) this.setQuality('good', `Bra bildkvalitet för vald storlek (cirka ${dpi} DPI).`);
    else if (dpi >= 130) this.setQuality('warning', `Godkänd kvalitet (cirka ${dpi} DPI). Undvik att zooma mer.`);
    else this.setQuality('bad', `Låg bildkvalitet (cirka ${dpi} DPI). Du kan beställa – vi granskar bilden före tryck.`);
  }
  setQuality(level, message) {
    this.qualityNodes.forEach((node) => { node.dataset.level = level; });
    this.qualityTextNodes.forEach((node) => { node.textContent = message; });
  }

  resolveVariant() {
    const selected = this.getSelectedOptions().map((input) => input.value);
    const variant = this.variants.find((item) => item.options.length === selected.length && item.options.every((option, index) => option === selected[index])) || (selected.length === 0 ? this.variants[0] : null);
    if (!variant) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Kombinationen saknas';
      return;
    }
    this.variantInput.value = variant.id;
    this.price.textContent = variant.price;
    this.submitButton.disabled = !variant.available || !this.image;
    this.submitButton.textContent = variant.available ? this.submitButton.dataset.defaultText : 'Slutsåld';
  }

  updateReview() {
    const style = this.querySelector('[data-mp-product-type]:checked');
    const text = this.querySelector('[data-mp-overlay-text]');
    const rows = [
      ['Orientering', this.orientation === 'landscape' ? 'Liggande' : 'Stående'],
      ['Utförande', style ? style.value : 'Framkalla ditt favoritfoto'],
      ...this.getSelectedOptions().map((input) => [input.dataset.optionName, input.value])
    ];
    if (this.hasWhiteBorder()) rows.push(['Vitkant', this.getBorderDescription()]);
    if (text && !text.disabled && text.value) rows.push(['Text', text.value]);
    if (text && !text.disabled) {
      rows.push(['Textdesign', this.textDesign]);
      rows.push(['Textfärg', this.querySelector('[data-mp-text-color]:checked')?.value || 'Svart']);
    }
    const dpi = this.calculateDpi();
    if (dpi) rows.push(['Bildkvalitet', `${dpi} DPI`]);
    this.review.innerHTML = rows.map(([label, value]) => `<li><span>${this.escapeHtml(label)}</span><strong>${this.escapeHtml(value)}</strong></li>`).join('');
    this.summaryInput.value = rows.map(([label, value]) => `${label}: ${value}`).join(' | ');
  }

  escapeHtml(value) { return String(value).replace(/[&<>'"]/g, (c) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' })[c]); }

  async prepareSubmit(event) {
    if (this.preparing) return;
    event.preventDefault(); event.stopImmediatePropagation(); this.clearError();
    if (!this.image || !this.sourceInput.files[0]) return this.showError('Ladda upp ett foto innan du beställer.');
    this.preparing = true;
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Förbereder bilderna…';
    this.updateReview();
    try {
      const blob = await this.createPrintBlob();
      const base = this.sourceInput.files[0].name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-');
      const file = new File([blob], `${base || 'foto'}-fardig.jpg`, { type: 'image/jpeg' });
      const transfer = new DataTransfer(); transfer.items.add(file); this.outputInput.files = transfer.files;
      this.preparing = false;
      HTMLFormElement.prototype.submit.call(this.form);
    } catch (error) {
      console.error('MP Photo Studio:', error);
      this.preparing = false; this.resolveVariant();
      this.showError('Bilden kunde inte förberedas. Prova en mindre bild eller en annan webbläsare.');
    }
  }

  createPrintBlob() {
    const [widthCm, heightCm] = this.getPhysicalSize();
    const longPixels = Math.min(5000, Math.max(1800, Math.max(this.image.naturalWidth, this.image.naturalHeight)));
    const ratio = widthCm / heightCm;
    let outputWidth = ratio >= 1 ? longPixels : Math.round(longPixels * ratio);
    let outputHeight = ratio >= 1 ? Math.round(longPixels / ratio) : longPixels;
    const output = document.createElement('canvas'); output.width = outputWidth; output.height = outputHeight;
    const context = output.getContext('2d', { alpha: false });
    context.fillStyle = '#fff'; context.fillRect(0, 0, outputWidth, outputHeight);
    const borders = this.getBordersCm();
    const innerX = Math.round(outputWidth * borders.left / widthCm);
    const innerY = Math.round(outputHeight * borders.top / heightCm);
    const innerRight = Math.round(outputWidth * borders.right / widthCm);
    const innerBottom = Math.round(outputHeight * borders.bottom / heightCm);
    const innerWidth = outputWidth - innerX - innerRight;
    const innerHeight = outputHeight - innerY - innerBottom;
    const scaleX = innerWidth / this.canvas.width;
    const scaleY = innerHeight / this.canvas.height;
    context.save();
    context.beginPath(); context.rect(innerX, innerY, innerWidth, innerHeight); context.clip();
    context.imageSmoothingEnabled = true; context.imageSmoothingQuality = 'high'; context.filter = this.filter;
    context.drawImage(this.image, innerX + this.offsetX * scaleX, innerY + this.offsetY * scaleY, this.image.naturalWidth * this.scale * scaleX, this.image.naturalHeight * this.scale * scaleY);
    context.restore(); context.filter = 'none';
    this.drawOverlayText(context, innerWidth, innerHeight, innerX, innerY);
    return new Promise((resolve, reject) => output.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Export failed')), 'image/jpeg', .94));
  }

  showError(message) { this.error.textContent = message; }
  clearError() { this.error.textContent = ''; }
}

if (!customElements.get('mp-photo-studio')) customElements.define('mp-photo-studio', MPPhotoStudio);

class MPStudentStudio extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mpReady === 'true') return;
    this.dataset.mpReady = 'true';
    this.form = this.querySelector('[data-mp-student-form]');
    this.variantInput = this.querySelector('[data-mp-student-variant-id]');
    this.price = this.querySelector('[data-mp-student-price]');
    this.submitButton = this.querySelector('[data-mp-student-submit]');
    this.fileInput = this.querySelector('[data-mp-student-file]');
    this.fileName = this.querySelector('[data-mp-student-filename]');
    this.outputInput = this.querySelector('[data-mp-student-output]');
    this.nameInput = this.querySelector('[data-mp-student-name]');
    this.classInput = this.querySelector('[data-mp-student-class]');
    this.yearInput = this.querySelector('[data-mp-student-year]');
    this.yearWrap = this.querySelector('[data-mp-student-year-wrap]');
    this.colorWrap = this.querySelector('[data-mp-student-color-wrap]');
    this.personalGroup = this.querySelector('[data-mp-student-personal-group]');
    this.designValue = this.querySelector('[data-mp-student-design-value]');
    this.summaryInput = this.querySelector('[data-mp-student-summary]');
    this.review = this.querySelector('[data-mp-student-review]');
    this.error = this.querySelector('[data-mp-student-error]');
    this.canvas = this.querySelector('[data-mp-student-canvas]');
    this.context = this.canvas?.getContext('2d', { alpha: false });
    this.uploadTitle = this.querySelector('[data-mp-student-upload-title]');
    this.uploadButton = this.querySelector('[data-mp-student-upload-button]');
    this.variants = JSON.parse(this.querySelector('[data-mp-student-variants]')?.textContent || '[]');
    this.design = 1;
    this.image = null;
    this.imageUrl = '';
    this.templates = {};
    this.preparing = false;

    this.querySelectorAll('[data-mp-student-option]').forEach((input) => input.addEventListener('change', () => this.update()));
    this.querySelectorAll('[data-mp-student-design]').forEach((input) => input.addEventListener('change', () => {
      this.design = Number(input.value);
      this.syncDesignOption();
      this.updateMode();
      this.update();
    }));
    this.querySelectorAll('[data-mp-student-color]').forEach((input) => input.addEventListener('change', () => this.update()));
    [this.nameInput, this.classInput, this.yearInput].forEach((input) => input?.addEventListener('input', () => this.update()));
    this.fileInput?.addEventListener('change', () => this.loadCustomerImage());
    this.querySelectorAll('[data-mp-student-preview-upload]').forEach((button) => button.addEventListener('click', () => this.fileInput?.click()));
    this.form?.addEventListener('submit', (event) => this.prepareSubmit(event));
    this.loadTemplates();
    if (document.fonts?.load) document.fonts.load('700 120px "Amatic SC"').then(() => this.draw()).catch(() => {});
    this.syncDesignOption();
    this.updateMode();
    this.update();
  }

  syncDesignOption() {
    const designOptions = [...this.querySelectorAll('[data-mp-student-option]')]
      .filter((input) => (input.dataset.optionName || '').trim().toLowerCase().includes('design'));
    if (!designOptions.length) return;
    const wanted = this.design === 6 ? ['design 6', 'egen färdig design', 'egen design'] : [`design ${this.design}`];
    const match = designOptions.find((input) => wanted.includes(input.value.trim().toLowerCase()));
    if (match) match.checked = true;
  }

  loadTemplates() {
    const urls = { 2: this.dataset.templateNr2, 3: this.dataset.templateNr3, 5: this.dataset.templateNr5 };
    Object.entries(urls).forEach(([key, url]) => {
      if (!url) return;
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => { this.templates[key] = image; this.update(); };
      image.onerror = () => { this.showError(`Dekorationerna för design ${key} kunde inte laddas. Ladda om sidan och försök igen.`); };
      image.src = url;
    });
  }

  async loadCustomerImage() {
    const file = this.fileInput?.files?.[0];
    this.clearError();
    if (!file) {
      this.image = null;
      this.fileName.textContent = 'JPG, PNG eller WebP · max 25 MB';
      this.classList.remove('has-image');
      this.update();
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      this.fileInput.value = '';
      this.showError('Bilden är större än 25 MB. Välj en mindre fil.');
      this.update();
      return;
    }
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
    this.imageUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      this.image = image;
      this.fileName.textContent = file.name;
      this.classList.add('has-image');
      this.update();
    };
    image.onerror = () => {
      this.image = null;
      this.showError('Bilden kunde inte öppnas. Välj en JPG-, PNG- eller WebP-fil.');
      this.update();
    };
    image.src = this.imageUrl;
  }

  getSelectedOptions() {
    return [...this.querySelectorAll('[data-mp-student-option]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition || 99) - Number(b.dataset.optionPosition || 99));
  }

  update() {
    const selected = this.getSelectedOptions().map((input) => input.value);
    const variant = this.variants.find((item) => item.options.length === selected.length && item.options.every((option, index) => option === selected[index])) || (selected.length === 0 ? this.variants[0] : null);
    if (variant) {
      this.variantInput.value = variant.id;
      this.price.textContent = variant.price;
    }
    const designName = this.design === 6 ? 'Egen färdig design' : `Design ${this.design}`;
    this.designValue.value = designName;
    const rows = [
      ...this.getSelectedOptions().map((input) => [input.dataset.optionName || 'Val', input.value]),
      ['Design', designName],
      ...(this.design === 6 ? [] : [['Namn', this.nameInput?.value || '—'], ['Klass', this.classInput?.value || '—']]),
      ...(this.design === 5 ? [['Årtal', this.yearInput?.value || '—']] : []),
      ...([1, 2, 4].includes(this.design) ? [['Textfärg', this.getTextColorLabel()]] : []),
      ['Bild', this.fileInput?.files?.[0]?.name || 'Ingen bild vald']
    ];
    this.review.innerHTML = rows.map(([label, value]) => `<li><span>${this.escapeHtml(label)}</span><strong>${this.escapeHtml(value)}</strong></li>`).join('');
    this.summaryInput.value = rows.map(([label, value]) => `${label}: ${value}`).join(' | ');
    const textReady = this.design === 6 || (
      this.nameInput?.value.trim() && this.nameInput.value.trim().toUpperCase() !== 'NAMN' &&
      this.classInput?.value.trim() && this.classInput.value.trim().toUpperCase() !== 'KLASS' &&
      (this.design !== 5 || /^\d{4}$/.test(this.yearInput?.value.trim() || ''))
    );
    const templateReady = ![2, 3, 5].includes(this.design) || Boolean(this.templates[this.design] && (this.design !== 3 || this.templates[2]));
    const ready = Boolean(variant?.available && this.image && textReady && templateReady && !this.preparing);
    this.submitButton.disabled = !ready;
    this.submitButton.textContent = this.preparing ? 'Förbereder bild…' : (variant && !variant.available ? 'Slutsåld' : this.submitButton.dataset.defaultText);
    this.draw();
  }

  updateMode() {
    const finished = this.design === 6;
    this.personalGroup.hidden = finished;
    this.nameInput.disabled = finished;
    this.classInput.disabled = finished;
    this.nameInput.required = !finished;
    this.classInput.required = !finished;
    this.yearWrap.hidden = this.design !== 5;
    this.yearInput.disabled = this.design !== 5;
    this.colorWrap.hidden = ![1, 2, 4].includes(this.design);
    this.querySelectorAll('[data-mp-student-color]').forEach((input) => { input.disabled = ![1, 2, 4].includes(this.design); });
    this.uploadTitle.textContent = finished ? 'Ladda upp egen färdig design' : 'Ladda upp foto';
    this.uploadButton.textContent = finished ? 'Välj färdig design' : 'Välj foto';
    const empty = this.querySelector('[data-mp-student-preview-upload]');
    if (empty) {
      empty.querySelector('strong').textContent = finished ? 'Ladda upp egen färdig design' : 'Ladda upp foto';
      empty.querySelector('span').textContent = finished ? 'Filen ska vara färdig i formatet 50 × 70 cm' : 'Fotot placeras automatiskt i vald design';
    }
  }

  draw() {
    if (!this.context || !this.canvas) return;
    this.render(this.context, this.canvas.width, this.canvas.height);
  }

  render(context, width, height) {
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, width, height);
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);

    if (this.design === 6) {
      if (this.image) this.drawContained(context, this.image, 0, 0, width, height, '#fff');
      else this.drawPlaceholder(context, 0, 0, width, height, 'LADDA UPP FÄRDIG DESIGN');
      context.restore();
      return;
    }

    if ([1, 2, 4].includes(this.design)) {
      if (this.image) this.drawCovered(context, this.image, 0, 0, width, height);
      else this.drawPlaceholder(context, 0, 0, width, height, 'LADDA UPP FOTO');
      if (this.design === 2 && this.templates[2]) this.drawContained(context, this.templates[2], 0, 0, width, height);
    }

    if (this.design === 3) {
      if (this.templates[3]) this.drawCovered(context, this.templates[3], 0, 0, width, height);
      else { context.fillStyle = '#dce7ec'; context.fillRect(0, 0, width, height); }
      const box = { x: width * .10, y: height * .19, w: width * .80, h: height * .7143 };
      if (this.image) this.drawCovered(context, this.image, box.x, box.y, box.w, box.h);
      else this.drawPlaceholder(context, box.x, box.y, box.w, box.h, 'LADDA UPP FOTO');
      if (this.templates[2]) this.drawContained(context, this.templates[2], 0, 0, width, height);
    }

    if (this.design === 5) {
      context.fillStyle = '#1b5baa'; context.fillRect(0, 0, width, height);
      const box = { x: width * .10, y: height * .19, w: width * .80, h: height * .7143 };
      if (this.image) this.drawCovered(context, this.image, box.x, box.y, box.w, box.h);
      else this.drawPlaceholder(context, box.x, box.y, box.w, box.h, 'LADDA UPP FOTO');
      if (this.templates[5]) this.drawContained(context, this.templates[5], 0, 0, width, height);
    }

    this.drawStudentText(context, width, height);
    context.restore();
  }

  drawStudentText(context, width, height) {
    const name = (this.nameInput?.value || 'NAMN').toUpperCase();
    const studentClass = (this.classInput?.value || 'KLASS').toUpperCase();
    const greeting = 'GRATTIS TILL STUDENTEN!';
    let color = [1, 2, 4].includes(this.design) ? this.getTextColor() : '#fff';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = color;
    context.strokeStyle = color === '#fff' ? 'rgba(0,0,0,.18)' : 'rgba(255,255,255,.18)';
    context.lineJoin = 'round';

    if (this.design === 5) {
      this.drawFittedText(context, name, width / 2, height * .065, width * .82, height * .060, '700 Arial, sans-serif');
      this.drawFittedText(context, studentClass, width / 2, height * .125, width * .82, height * .035, '700 Arial, sans-serif');
      this.drawFittedText(context, `STUDENT ${this.yearInput?.value || '2027'}`, width / 2, height * .955, width * .82, height * .052, '700 Arial, sans-serif');
      return;
    }

    const positions = {
      1: [.79, .865, .95],
      2: [.79, .865, .95],
      3: [.115, .865, .95],
      4: [.09, .865, .95]
    }[this.design] || [.79, .865, .95];
    this.drawFittedText(context, name, width / 2, height * positions[0], width * .90, height * .13, '700 "Amatic SC", sans-serif');
    this.drawFittedText(context, studentClass, width / 2, height * positions[1], width * .88, height * .078, '700 "Amatic SC", sans-serif');
    this.drawFittedText(context, greeting, width / 2, height * positions[2], width * .94, height * .082, '700 "Amatic SC", sans-serif');
  }

  drawFittedText(context, text, x, y, maxWidth, size, family) {
    let fontSize = Math.max(14, size);
    const suffix = family.replace(/^\d+\s+/, '');
    context.font = `${family.match(/^\d+/)?.[0] || 700} ${fontSize}px ${suffix}`;
    while (fontSize > 14 && context.measureText(text).width > maxWidth) {
      fontSize -= 2;
      context.font = `${family.match(/^\d+/)?.[0] || 700} ${fontSize}px ${suffix}`;
    }
    context.lineWidth = Math.max(1, fontSize * .015);
    context.strokeText(text, x, y);
    context.fillText(text, x, y);
  }

  drawCovered(context, image, x, y, width, height) {
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  }

  drawContained(context, image, x, y, width, height, background = null) {
    if (background) { context.fillStyle = background; context.fillRect(x, y, width, height); }
    const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  }

  drawPlaceholder(context, x, y, width, height, label) {
    context.fillStyle = '#eeeeec'; context.fillRect(x, y, width, height);
    context.strokeStyle = 'rgba(0,0,0,.24)'; context.lineWidth = Math.max(1, width * .003); context.setLineDash([12, 12]);
    context.strokeRect(x + width * .04, y + height * .04, width * .92, height * .92); context.setLineDash([]);
    context.fillStyle = 'rgba(0,0,0,.52)'; context.textAlign = 'center'; context.textBaseline = 'middle';
    context.font = `700 ${Math.max(14, width * .045)}px Arial, sans-serif`; context.fillText(label, x + width / 2, y + height / 2);
  }

  getTextColor() {
    return this.querySelector('[data-mp-student-color]:checked')?.dataset.color || '#ffffff';
  }

  getTextColorLabel() {
    return this.querySelector('[data-mp-student-color]:checked')?.value || 'Vit';
  }

  async prepareSubmit(event) {
    event.preventDefault();
    if (this.preparing || this.submitButton.disabled) return;
    this.preparing = true;
    this.clearError();
    this.update();
    try {
      const output = document.createElement('canvas');
      output.width = 4016;
      output.height = 5622;
      const context = output.getContext('2d', { alpha: false });
      this.render(context, output.width, output.height);
      const blob = await new Promise((resolve, reject) => output.toBlob((result) => result ? resolve(result) : reject(new Error('Export failed')), 'image/jpeg', .94));
      const fileName = this.design === 6 ? 'egen-fardig-design.jpg' : `studentskylt-design-${this.design}.jpg`;
      const transfer = new DataTransfer();
      transfer.items.add(new File([blob], fileName, { type: 'image/jpeg' }));
      this.outputInput.files = transfer.files;
      HTMLFormElement.prototype.submit.call(this.form);
    } catch (error) {
      console.error('MP Student Studio:', error);
      this.preparing = false;
      this.showError('Den färdiga studentskylten kunde inte skapas. Prova att ladda om sidan eller välj en mindre bild.');
      this.update();
    }
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>'"]/g, (character) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' })[character]);
  }

  showError(message) { if (this.error) this.error.textContent = message; }
  clearError() { if (this.error) this.error.textContent = ''; }
}

if (!customElements.get('mp-student-studio')) customElements.define('mp-student-studio', MPStudentStudio);

})();

